import {
  Button,
  Divider,
  FlexBox,
  Icon,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogNav,
  ModalDialogTitle,
  Typography,
  VisuallyHidden,
} from '@vp/swan'
import { useCallback } from 'react'
import { isVatEnabled } from '../../../utils/vat-util'
import { VatToggle } from '../VatToggle'
import type { MobileMenuProps } from '.'
import PrimaryPanel from './panels/PrimaryPanel'
import { NavCategoryPanel } from '../navigation/mobile'
import { MyAccountUserPop } from '../my-account/UserPopUp'
import { useAuth } from '../../../hooks/useAuth'
import { useMobileMenuNavigation } from '../../../hooks/useMobileMenuNavigation'
import { useI18n } from '../../../context/TranslationContext'
import { useNavContext } from '../../../context/NavContextProvider'

export type { PanelType, OpenPanelParams } from '../../../hooks/useMobileMenuNavigation'

interface MobileMenuSidebarProps extends MobileMenuProps {
  handleClose: () => void
  showMenu: boolean
}

export default function MobileMenuSidebar(props: Readonly<MobileMenuSidebarProps>) {
  const { helpLink, myProjectsLink, navigationData, handleClose, brands, brandsDesktop, brandsHeading, isRedesignedSignInButtonEnabled, showMenu, locale } =
    props
  const { isMxNav } = useNavContext()
  const { translate } = useI18n()
  const { profile } = useAuth()
  const { activePanel, title, activeNavData, openPanel, resetNavState } = useMobileMenuNavigation(navigationData)
  const country = locale.split('-')[1]?.toUpperCase() || ''

  const handleRequestDismiss = useCallback(() => {
    resetNavState()
    handleClose()
  }, [resetNavState, handleClose])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleRequestDismiss()
      }
    },
    [handleRequestDismiss],
  )

  const getMenuAnnouncement = () => {
    if (activePanel === 'primary') {
      return translate('menu.main')
    }
    return title ? `${title} ${translate('menu.sub')}` : translate('menu.menu')
  }

  return (
    <ModalDialog variant="menu" bodyWidth={isMxNav ? 'capped' : 'standard'} isOpen={showMenu} onRequestDismiss={handleRequestDismiss}>
      <ModalDialogContent aria-label={translate('menu.navigation')} onKeyDown={handleKeyDown}>
        {/* Accessibility announcement for screen readers */}
        <VisuallyHidden role="status" aria-live="polite">
          {getMenuAnnouncement()}
        </VisuallyHidden>

        {/* Back button & panel title */}
        <ModalDialogNav>
          {title && (
            <FlexBox alignItems="center" gap="between-icon-and-text">
              <Button skin="unstyled" onClick={resetNavState} aria-label={translate('menu.goBackMain')}>
                <Icon iconType="arrowLeft" size="standard" aria-label={translate('menu.goBackMain')} />
              </Button>
              <ModalDialogTitle>
                <Typography fontSkin="body-standard-bold">{title}</Typography>
              </ModalDialogTitle>
            </FlexBox>
          )}
          <ModalDialogCloseButton aria-label={translate('close')} />
        </ModalDialogNav>
        <Divider />

        {/* Active panel */}
        <ModalDialogBody>
          {activePanel === 'primary' && (
            <PrimaryPanel
              helpLink={helpLink}
              myProjectsLink={myProjectsLink}
              navigationData={navigationData}
              openPanel={openPanel}
              brands={brands}
              brandsDesktop={brandsDesktop}
              brandsHeading={brandsHeading}
              isRedesignedSignInButtonEnabled={isRedesignedSignInButtonEnabled}
            />
          )}
          {activePanel === 'navCategory' && activeNavData && <NavCategoryPanel activeNavData={activeNavData} />}
          {activePanel === 'account' && <MyAccountUserPop profile={profile} placement="sidebar" />}
        </ModalDialogBody>

        {/* VAT toggle footer */}
        {isVatEnabled(country) && (
          <ModalDialogFooter pinned style={{ marginInline: '-14px' }} pl={5}>
            <ModalDialogButtons>
              <VatToggle vatPlacement="mobile-menu" locale={locale} />
            </ModalDialogButtons>
          </ModalDialogFooter>
        )}
      </ModalDialogContent>
    </ModalDialog>
  )
}
